import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import cookies from 'js-cookie';

import { CardWrapper, cookieNames } from '@monorepo/common';

import moment from 'moment';
import { useSaveStepsDataMutation } from '../../../services/servicePostAJob';
import { StatusButtonOptions } from '../components/StatusButtonOptions';
import { formattedLocations } from '../utils/formattedlocation';
import { showDropButton } from '../../../features/sliceStepper';
import { staticValuesForPaymentStep } from '../staticValues';
import { CopyOptions } from '../components/CopyOptions';
import { FirstLine } from '../components/FirstLine';
import { book } from '../../../routers/books';
import { IEditJobData } from '../interfaces';
import { useAppSelector } from '../../../store/hooks';
import { Hosts } from '../../../constants/hostName';
import { useStyles } from '../styles';
import { useReactivateFreeJobMutation, useStopJobMutation } from '../../../services/serviceJobData';
import { showNotification } from '../../../features/sliceNotification';
import { getUpgradeJobPath } from '../../../utils/jobAccountRoutes';
import {
  ACCESS_REMOVED_MESSAGE,
  isForbidden,
} from '../../../utils/jobAccessError';
import { allowLeavingPage } from '../../../components/LeavePageGuard';

interface IStepperInfoPanelProps {
  guid: string;
  data: IEditJobData;
  hasUnsavedChanges?: boolean;
}
export const StepperInfoPanel = ({ guid, data, hasUnsavedChanges }: IStepperInfoPanelProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { push } = useHistory();
  const [isLocalLoading, setIsLocalLoading] = useState<boolean>(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState<boolean>(false);
  const [saveStepsData, { data: savedData, isSuccess, isError }] = useSaveStepsDataMutation();
  const [reactivateFreeJob, { isSuccess: reactivateSuccess }] = useReactivateFreeJobMutation();
  const [
    stopJob,
    {
      error: stopError,
      isLoading: isStopping,
      isSuccess: stopSuccess,
    },
  ] = useStopJobMutation();

  const { siteId } = useAppSelector(({ pathnameSettings }) => ({
    siteId: pathnameSettings.siteNumber
  }));

  const siteRedirectUpgrade = useCallback(
    () => getUpgradeJobPath(siteId, guid),
    [siteId, guid]
  );

  const siteCopyRedirect = useCallback(() => {
    if (Hosts.BREWBOUND === siteId) {
      return book.postAJobBrewbound;
    }
    if (Hosts.NOSH === siteId) {
      return book.postAJobNosh;
    }
    return book.postAJob;
  }, [siteId]);

  const copyJob = useCallback(() => {
    if (data) {
      const applicationEmailsData:{ email: string }[] = [];

      if (data.stepper.step5.applicationEmail.length > 0) {
        data.stepper.step5.applicationEmail
          .split(';')
          .map((e) => e.trim())
          .filter(Boolean)
          .forEach((item) => {
            applicationEmailsData.push({
              email: item
            });
          });
      }
      setIsLocalLoading(true);
      cookies.remove(cookieNames.tempJob);
      saveStepsData({
        step1: {
          // Preserve the source listing's type so copying a Featured listing
          // starts a Featured post (2), not Standard (1). See D-24094.
          listingType: data.header.isFeatured ? 2 : 1,
        },
        step2: data.stepper.step1,
        step3: {
          ...data.stepper.step2,
          // JobInfo never sends `noneLocation`, so derive it: a source listing with
          // no saved locations is remote, and the copy must start out remote too.
          noneLocation: !data.stepper.step2.locations.length,
          locations: formattedLocations(data.stepper.step2.locations)
        },
        step4: data.stepper.step3,
        step5: data.stepper.step4,
        step6: {
          ...data.stepper.step5,
          ...(!data.stepper.step5.tempAdditionalEmails) && {
            tempAdditionalEmails: [{ email: null }]
          },
          ...(data.stepper.step5.applicationEmail.length) && {
            tempAdditionalEmails: applicationEmailsData,
            showEmails: true,
            applicationUrlEnabled: false
          }
        },
        step7: staticValuesForPaymentStep
      });
    }
  }, [data]);

  const reactivateFreeJobRequest = useCallback(() => {
    if (data) {
      reactivateFreeJob(guid);
    }
  }, [data]);

  const stopJobRequest = useCallback(async () => {
    if (data) {
      const result = await stopJob(guid);
      if ('data' in result) {
        setIsDeactivateOpen(false);
      }
    }
  }, [data, guid, stopJob]);

  useEffect(() => {
    if (reactivateSuccess) {
      dispatch(showNotification({
        show: true,
        type: 'success',
        text: 'Job was successfully reactivated!'
      }));
    }
  }, [reactivateSuccess]);

  useEffect(() => {
    if (stopSuccess) {
      dispatch(showNotification({
        show: true,
        type: 'success',
        text: 'Job was successfully deactivated!'
      }));
    }
  }, [stopSuccess]);

  useEffect(() => {
    if (isForbidden(stopError)) {
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: ACCESS_REMOVED_MESSAGE
      }));
    }
  }, [dispatch, stopError]);

  useEffect(() => {
    if (savedData && savedData.guid && isSuccess) {
      cookies.set(cookieNames.tempJob, savedData.guid);
      dispatch(showDropButton());

      const timeout = setTimeout(() => {
        const url = siteCopyRedirect();
        // The copy dialog already warned about unsaved changes, so the leave guard must not
        // stop this move: a cancel there would strand the dialog's loader.
        allowLeavingPage(url);
        push(url);
        clearTimeout(timeout);
      }, 1000);
    }
  }, [savedData, isSuccess]);

  useEffect(() => {
    if (isError) {
      setIsLocalLoading(false);
    }
  }, [isError]);

  return (
    <CardWrapper>
      <Typography
        fontWeight={700}
        variant="subtitle1"
      >
        Edit Listing:
      </Typography>
      <Typography
        variant="h4"
        marginBottom={0.5}
      >
        {data.stepper.step1.jobTitle}
      </Typography>

      <FirstLine data={data.header} />

      <Button
        component={Link}
        to={book.jobsListings}
        variant="outlined"
        size="small"
        sx={{ my: 2 }}
      >
        Back to Job Listings
      </Button>

      <div className={classes.row}>

        <StatusButtonOptions
          guid={guid}
          data={data}
          upgradeListing={siteRedirectUpgrade}
          reactivateFreeJobRequest={reactivateFreeJobRequest}
        />
        <CopyOptions
          guid={guid}
          copyJob={copyJob}
          isLoading={isLocalLoading}
          hasUnsavedChanges={hasUnsavedChanges}
          title={data?.stepper.step1.jobTitle}
        />
      </div>

      {!data.header.isReactivateAllowed && moment(data.header.expiryDate).isAfter(moment()) && (
        <>
          <Typography
            className={classes.comment_bottom}
          >
            To make changes to the contents of the listings, please    go through the steps below or select to deactivate this listing.
          </Typography>
          <Button
            className={classes.deactivate_btn}
            // Spacing lives here (v5 sx), not in the v4 makeStyles class: a JSS
            // `marginTop` on this v5 Button gets beaten by the Button's own emotion
            // styles, so the gap never showed. sx is the same styling system.
            sx={{ mt: 2 }}
            variant="outlined"
            color="error"
            onClick={() => setIsDeactivateOpen(true)}
          >
            Deactivate Listing
          </Button>
        </>
      )}

      <Dialog
        open={isDeactivateOpen}
        onClose={() => setIsDeactivateOpen(false)}
        aria-labelledby="deactivate-listing-title"
      >
        <DialogTitle id="deactivate-listing-title">
          Deactivate Listing?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This listing will immediately stop appearing to applicants. You can
            reactivate it later if it is still eligible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeactivateOpen(false)} disabled={isStopping}>
            Cancel
          </Button>
          <Button
            onClick={stopJobRequest}
            color="error"
            variant="contained"
            disabled={isStopping}
          >
            {isStopping ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </CardWrapper>
  );
};
